import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle, ArrowLeft, CreditCard, HelpCircle, MessageCircle, RefreshCw } from 'lucide-react';
import { PAYPAL_CREATE_ORDER_URL } from '../../config/api';
import '../PaymentCancelled/PaymentCancelled.css';

interface CancellationDetails {
  token: string | null;
  order_id: string | null;
  reason: string;
  planType: string;
  amount: string;
  currency: string;
  timestamp: string;
}

const PaymentCancelled: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cancellationDetails, setCancellationDetails] = useState<CancellationDetails | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState<'paypal' | null>(null);

  useEffect(() => {
    // Get cancellation details from URL params and localStorage
    const token = searchParams.get('token');
    const reason = searchParams.get('reason') || 'user_cancelled';
    const storedOrderId = localStorage.getItem('currentOrderId');
    const planType = localStorage.getItem('currentOrderPlan') || 'monthly';
    const amount = localStorage.getItem('currentOrderAmount') || '4.99';
    const currency = localStorage.getItem('currentOrderCurrency') || 'GBP';

    setPaymentProvider('paypal');

    setCancellationDetails({
      token,
      order_id: storedOrderId,
      reason,
      planType,
      amount,
      currency,
      timestamp: new Date().toLocaleString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    });

    // Clean up localStorage after a delay
    const timer = setTimeout(() => {
      localStorage.removeItem('currentOrderPlan');
      localStorage.removeItem('currentOrderAmount');
      localStorage.removeItem('currentOrderReference');
      localStorage.removeItem('currentOrderId');
      localStorage.removeItem('currentOrderCurrency');
      localStorage.removeItem('currentChargedAmount');
      localStorage.removeItem('currentChargedCurrency');
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, [searchParams]);

  const handleTryAgain = async () => {
    if (!cancellationDetails || isRetrying) return;

    setIsRetrying(true);

    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        alert('Please log in to continue with payment');
        navigate('/login');
        return;
      }

      // Determine the correct domain for return URLs
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const currentDomain = isDevelopment ? 'http://localhost:3000' : 'https://homecookt.com';
      
      // Get plan type mapping
      const planTypeMapping = {
        weekly: 'WEEKLY',
        monthly: 'MONTHLY', 
        yearly: 'YEARLY'
      };
      
      const planType = planTypeMapping[cancellationDetails.planType as keyof typeof planTypeMapping] || 'MONTHLY';
      
      console.log('Retrying PayPal payment with plan:', planType);
      
      // Create new PayPal order
      const response = await fetch(PAYPAL_CREATE_ORDER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          plan_type: planType,
          return_url: `${currentDomain}/payment-success`,
          cancel_url: `${currentDomain}/payment-cancelled`,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store the new order details
        localStorage.setItem('currentOrderPlan', cancellationDetails.planType);
        localStorage.setItem('currentOrderId', data.order_id);
        localStorage.setItem('currentOrderAmount', data.display_amount?.toString() || cancellationDetails.amount);
        localStorage.setItem('currentOrderCurrency', data.display_currency || cancellationDetails.currency);
        localStorage.setItem('currentChargedAmount', data.charged_amount?.toString() || cancellationDetails.amount);
        localStorage.setItem('currentChargedCurrency', data.charged_currency || cancellationDetails.currency);
        localStorage.setItem('currentOrderStatus', data.status || 'CREATED');
        localStorage.setItem('currentUserId', data.user_id?.toString() || '');
        
        console.log('New PayPal order created successfully:', data);
        console.log('Redirecting to:', data.approval_link);
        
        // Redirect to PayPal approval URL
        window.location.href = data.approval_link;
      } else {
        // Handle API errors
        if (response.status === 400) {
          if (data.message && data.message.toLowerCase().includes('active subscription')) {
            alert('You already have an active subscription. Please contact support if you need to change your plan.');
          } else {
            alert(data.message || 'Invalid plan type or subscription already exists.');
          }
        } else if (response.status === 401) {
          alert('Authentication failed. Please log in again.');
          navigate('/login');
        } else if (response.status === 404) {
          alert('Payment service not found. Please try again later.');
        } else if (response.status === 500) {
          alert('Server error occurred. Please try again later or contact support.');
        } else {
          alert(data.message || 'Failed to create PayPal order. Please try again.');
        }
      }
    } catch (error) {
      console.error('PayPal retry error:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleGoHome = () => {
    // Clear any remaining order data
    localStorage.removeItem('currentOrderPlan');
    localStorage.removeItem('currentOrderAmount');
    localStorage.removeItem('currentOrderReference');
    localStorage.removeItem('currentOrderId');
    localStorage.removeItem('currentOrderCurrency');
    localStorage.removeItem('currentChargedAmount');
    localStorage.removeItem('currentChargedCurrency');
    localStorage.removeItem('currentOrderStatus');
    localStorage.removeItem('currentUserId');
    
    // Navigate to home page
    navigate('/');
  };

  const handleContactSupport = () => {
    const message = `Hi, I need help with my cancelled payment on HomeCookt.%0A%0ADetails:%0A- Order ID: ${cancellationDetails?.order_id || 'N/A'}%0A- PayPal Token: ${cancellationDetails?.token || 'N/A'}%0A- Plan: ${cancellationDetails?.planType || 'N/A'}%0A- Amount: ${cancellationDetails?.currency} ${cancellationDetails?.amount || 'N/A'}%0A- Time: ${cancellationDetails?.timestamp || 'N/A'}%0A- Reason: ${getCancellationReason(cancellationDetails?.reason || 'user_cancelled')}`;
    
    // Open WhatsApp with pre-filled message
    window.open(`https://wa.me/447737024736?text=${message}`, '_blank');
  };

  const handleBankTransfer = () => {
    if (!cancellationDetails) return;

    // Generate a bank transfer reference for bank payments
    const timestamp = Date.now().toString().slice(-8);
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const planCode = cancellationDetails.planType ? cancellationDetails.planType.charAt(0).toUpperCase() : 'M';
    const bankReference = `HC${planCode}${timestamp}${randomNum}`;
    
    // Store bank transfer details
    localStorage.setItem('currentOrderReference', bankReference);
    localStorage.setItem('currentOrderPlan', cancellationDetails.planType);
    localStorage.setItem('currentOrderAmount', cancellationDetails.amount);
    localStorage.setItem('currentOrderCurrency', cancellationDetails.currency);
    
    // Navigate to payment methods page
    navigate('/payment-methods');
  };

  const getCancellationReason = (reason: string) => {
    switch (reason) {
      case 'user_cancelled':
        return 'You cancelled the payment process';
      case 'session_timeout':
        return 'Payment session timed out';
      case 'payment_declined':
        return 'Payment method was declined';
      case 'technical_error':
        return 'Technical error occurred';
      default:
        return 'Payment was cancelled';
    }
  };

  return (
    <div className="payment-cancelled-page">
      <div className="cancelled-container">
        {/* Animated Cancel Icon */}
        <div className="cancelled-icon-wrapper">
          <div className="cancelled-icon">
            <XCircle size={80} />
          </div>
          <div className="cancelled-ripple"></div>
        </div>

        {/* Cancel Message */}
        <div className="cancelled-content">
          <h1 className="cancelled-title">Payment Cancelled</h1>
          <p className="cancelled-subtitle">
            {getCancellationReason(cancellationDetails?.reason || 'user_cancelled')}. No charges were made to your account.
            {paymentProvider === 'paypal' && ' You can try again with PayPal or choose a different payment method.'}
          </p>
        </div>

        {/* Cancellation Details */}
        {cancellationDetails && (
          <div className="cancellation-details-card">
            <h3>Cancellation Details</h3>
            <div className="cancellation-details-grid">
              <div className="detail-item">
                <span className="label">Plan:</span>
                <span className="value">{cancellationDetails.planType.charAt(0).toUpperCase() + cancellationDetails.planType.slice(1)} Subscription</span>
              </div>
              <div className="detail-item">
                <span className="label">Amount:</span>
                <span className="value">{cancellationDetails.currency} {cancellationDetails.amount}</span>
              </div>
              <div className="detail-item">
                <span className="label">Cancelled At:</span>
                <span className="value">{cancellationDetails.timestamp}</span>
              </div>
              {cancellationDetails.order_id && (
                <div className="detail-item">
                  <span className="label">Order ID:</span>
                  <span className="value reference">{cancellationDetails.order_id}</span>
                </div>
              )}
              {cancellationDetails.token && (
                <div className="detail-item">
                  <span className="label">PayPal Token:</span>
                  <span className="value reference">{cancellationDetails.token}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reason Section */}
        <div className="cancellation-info">
          <h3>Common reasons for cancellation:</h3>
          <ul className="reason-list">
            <li>Payment was cancelled by user</li>
            <li>Session timeout during payment</li>
            <li>Payment method declined</li>
            <li>Browser or network issues</li>
          </ul>
        </div>

        {/* Alternative Options */}
        <div className="alternatives-section">
          <h3>What would you like to do?</h3>
          <div className="alternatives-grid">
            <div className="alternative-card primary">
              <div className="alternative-icon retry">
                <RefreshCw size={24} />
              </div>
              <h4>Try Again</h4>
              <p>Retry your payment with PayPal</p>
              <button 
                onClick={handleTryAgain} 
                disabled={isRetrying}
                className="alternative-btn primary"
              >
                {isRetrying ? 'Creating Order...' : 'Retry Payment'}
              </button>
            </div>
            
            <div className="alternative-card">
              <div className="alternative-icon bank">
                <CreditCard size={24} />
              </div>
              <h4>Bank Transfer</h4>
              <p>Pay securely via bank transfer</p>
              <button onClick={handleBankTransfer} className="alternative-btn">
                Bank Transfer
              </button>
            </div>
            
            <div className="alternative-card">
              <div className="alternative-icon support">
                <MessageCircle size={24} />
              </div>
              <h4>Get Help</h4>
              <p>Contact our support team</p>
              <button onClick={handleContactSupport} className="alternative-btn">
                Contact Support
              </button>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="faq-section">
          <h3>
            <HelpCircle size={20} />
            Frequently Asked Questions
          </h3>
          <div className="faq-list">
            <div className="faq-item">
              <h4>Was I charged for the cancelled payment?</h4>
              <p>No, cancelled payments are not charged. Your account remains unchanged and no money was deducted.</p>
            </div>
            <div className="faq-item">
              <h4>Can I try a different payment method?</h4>
              <p>Yes, you can retry with PayPal or use our secure bank transfer option for the same plan.</p>
            </div>
            <div className="faq-item">
              <h4>How long does bank transfer take?</h4>
              <p>Bank transfers typically take 1-3 business days to process and activate your subscription.</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="cancelled-actions">
          <button onClick={handleGoHome} className="secondary-action">
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <button 
            onClick={handleTryAgain} 
            disabled={isRetrying}
            className="primary-action"
          >
            <RefreshCw size={20} />
            {isRetrying ? 'Creating Order...' : 'Try Again'}
          </button>
        </div>

        {/* Footer */}
        <div className="cancelled-footer">
          <p>Need immediate assistance? We're here to help!</p>
          <div className="contact-options">
            <a href="mailto:Homecookt.com@gmail.com" aria-label="Email" target="blank" className='contact-link'>
              Email Support
            </a>
            <span className="separator">•</span>
            <a 
              href={`https://wa.me/447737024736?text=Hi, I need help with my cancelled payment on HomeCookt. Order ID: ${cancellationDetails?.order_id || 'N/A'}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="contact-link"
            >
              WhatsApp Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCancelled;